import { db } from '../config/firebase';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import {
  buildAdminPendingEnrollmentList,
  buildAdminPendingPaymentList,
  buildCourseBatchSeatSummary,
  buildStudentCourseMap,
  buildStudentEnrollmentStatusMap,
  formatStudentName,
  toArray,
} from '../utils/lmsSelectors';
import { getCourseSuspension, getCourseSuspensions } from '../utils/accessSuspensions';

export const buildBranchCatalogsFromSettings = (raw) => {
  const map = raw || {};
  const list = Object.entries(map).map(([id, value]) => ({
    ...value,
    id: value?.id || id,
  }));
  const byId = {};
  const bySlug = {};
  list.forEach((branch) => {
    byId[branch.id] = branch;
    if (branch.slug) bySlug[branch.slug] = branch;
  });
  return { byId, bySlug, list };
};

export async function loadAdminWorkspaceRaw() {
  const [
    coursesSnapshot,
    studentsSnapshot,
    usersSnapshot,
    enrollmentsSnapshot,
    requestsSnapshot,
    paymentsSnapshot,
    paymentRequestsSnapshot,
    attendanceSnapshot,
    branchesSnapshot,
  ] = await Promise.all([
    get(ref(db, 'courses')),
    get(ref(db, 'students')),
    get(ref(db, 'users')),
    get(ref(db, 'enrollments')),
    get(ref(db, 'enrollmentRequests')),
    get(ref(db, 'payments')),
    get(ref(db, 'paymentRequests')),
    get(ref(db, 'attendance')),
    get(ref(db, 'settings/branches')),
  ]);

  const courses = coursesSnapshot.val() || {};
  const students = studentsSnapshot.val() || {};
  const users = usersSnapshot.val() || {};
  const enrollments = toArray(enrollmentsSnapshot.val());
  const enrollmentRequests = toArray(requestsSnapshot.val());
  const payments = toArray(paymentsSnapshot.val());
  const paymentRequests = toArray(paymentRequestsSnapshot.val());
  const branchCatalogs = buildBranchCatalogsFromSettings(branchesSnapshot.val());
  const attendanceByStudent = Object.fromEntries(
    Object.entries(attendanceSnapshot.val() || {}).map(([studentId, records]) => [
      studentId,
      toArray(records),
    ]),
  );

  return {
    courses,
    students,
    users,
    enrollments,
    enrollmentRequests,
    payments,
    paymentRequests,
    attendanceByStudent,
    branchCatalogs,
  };
}

export function buildAdminCoursesOverview(workspace) {
  const {
    courses,
    students,
    users,
    enrollments,
    payments,
    branchCatalogs,
  } = workspace;

  const courseList = toArray(courses).map((course) => ({
    ...course,
    seatSummary: buildCourseBatchSeatSummary(course, enrollments, {
      students,
      users,
      branchCatalogs,
    }),
    activeEnrollmentCount: enrollments.filter(
      (enrollment) =>
        enrollment.courseId === course.id && enrollment.status === 'active',
    ).length,
  }));

  const recentEnrollments = [...enrollments]
    .sort((a, b) => new Date(b.enrollmentDate) - new Date(a.enrollmentDate))
    .slice(0, 5)
    .map((enrollment) => ({
      ...enrollment,
      studentName:
        formatStudentName(students[enrollment.studentId]) ||
        formatStudentName(users[enrollment.studentId]) ||
        'Unknown student',
      courseName: courses[enrollment.courseId]?.name || 'Unknown course',
    }));

  const monthlyRevenue = payments.reduce((acc, payment) => {
    if (payment.status !== 'completed' || !payment.paymentDate) return acc;

    const date = new Date(payment.paymentDate);
    const key = `${date.getMonth() + 1}/${date.getFullYear()}`;
    acc[key] = (acc[key] || 0) + Number(payment.amount || 0);
    return acc;
  }, {});

  return {
    courses,
    courseList,
    students,
    users,
    enrollments,
    payments,
    stats: {
      totalStudents: Object.keys(students).length,
      activeCourses: courseList.filter((course) => course.status === 'active').length,
      totalRevenue: payments
        .filter((payment) => payment.status === 'completed')
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
      pendingPayments: payments.filter((payment) => payment.status === 'pending').length,
    },
    recentEnrollments,
    monthlyRevenue: Object.entries(monthlyRevenue)
      .map(([month, revenue]) => ({ month, revenue }))
      .sort((a, b) => {
        const [aMonth, aYear] = a.month.split('/');
        const [bMonth, bYear] = b.month.split('/');
        return new Date(aYear, aMonth - 1) - new Date(bYear, bMonth - 1);
      }),
  };
}

export function buildAdminEnrollmentQueue(workspace) {
  const { courses, students, users, enrollments, enrollmentRequests, branchCatalogs } =
    workspace;

  return {
    courses,
    students,
    users,
    enrollments,
    enrollmentRequests,
    pendingRequests: buildAdminPendingEnrollmentList({
      courses,
      enrollments,
      enrollmentRequests,
      students,
    }),
    courseList: toArray(courses).map((course) => ({
      ...course,
      seatSummary: buildCourseBatchSeatSummary(course, enrollments, {
        students,
        users,
        branchCatalogs,
      }),
    })),
  };
}

export function buildAdminPaymentsOverview(workspace) {
  const { payments, paymentRequests, courses, students } = workspace;

  return {
    payments: [...payments].sort(
      (a, b) => new Date(b.paymentDate) - new Date(a.paymentDate),
    ),
    paymentRequests: [...paymentRequests].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    ),
    pendingPayments: buildAdminPendingPaymentList({
      payments,
      paymentRequests,
      students,
      courses,
    }),
    courses,
    students,
  };
}

export function buildAdminBatchLedger(workspace) {
  const {
    courses,
    students,
    users,
    enrollments,
    payments,
    paymentRequests,
    attendanceByStudent,
    branchCatalogs,
  } = workspace;

  return {
    courses,
    courseList: toArray(courses).map((course) => ({
      ...course,
      seatSummary: buildCourseBatchSeatSummary(course, enrollments, {
        students,
        users,
        branchCatalogs,
      }),
    })),
    students,
    users,
    enrollments,
    payments,
    paymentRequests,
    attendanceByStudent,
  };
}

export async function loadStudentProfileRaw(userId) {
  const [userSnapshot, studentSnapshot] = await Promise.all([
    get(ref(db, `users/${userId}`)),
    get(ref(db, `students/${userId}`)),
  ]);

  return {
    user: userSnapshot.val() || null,
    student: studentSnapshot.val() || null,
  };
}

export async function loadStudentWorkspaceRaw(userId) {
  const [
    coursesSnapshot,
    enrollmentsSnapshot,
    requestsSnapshot,
    paymentRequestsSnapshot,
    paymentsSnapshot,
    userSnapshot,
    studentSnapshot,
  ] = await Promise.all([
    get(ref(db, 'courses')),
    get(query(ref(db, 'enrollments'), orderByChild('studentId'), equalTo(userId))),
    get(
      query(ref(db, 'enrollmentRequests'), orderByChild('studentId'), equalTo(userId)),
    ),
    get(
      query(ref(db, 'paymentRequests'), orderByChild('studentId'), equalTo(userId)),
    ),
    get(query(ref(db, 'payments'), orderByChild('studentId'), equalTo(userId))),
    get(ref(db, `users/${userId}`)),
    get(ref(db, `students/${userId}`)),
  ]);

  const courses = coursesSnapshot.val() || {};
  const enrollments = toArray(enrollmentsSnapshot.val());
  const enrollmentRequests = toArray(requestsSnapshot.val());
  const paymentRequests = toArray(paymentRequestsSnapshot.val());
  const payments = toArray(paymentsSnapshot.val());
  const user = userSnapshot.val() || {};
  const student = studentSnapshot.val() || {};
  const courseMap = buildStudentCourseMap(courses, enrollments);
  Object.values(courseMap).forEach((course) => {
    course.courseSuspension = getCourseSuspension({
      courseId: course.id,
      user,
      student,
      enrollment: course.enrollment,
    });
  });
  const statusMap = buildStudentEnrollmentStatusMap({
    enrollments,
    enrollmentRequests,
    paymentRequests,
    payments,
  });
  const allCourses = toArray(courses).map((course) => ({
    ...course,
    status: statusMap[course.id] || 'available',
    seatSummary: buildCourseBatchSeatSummary(course, enrollments),
  }));

  return {
    allCourses,
    activeCourses: Object.values(courseMap),
    courseMap,
    statusMap,
    enrollments,
    enrollmentRequests,
    paymentRequests,
    payments,
    courses,
    user,
    student,
    courseSuspensions: getCourseSuspensions(user, student),
  };
}

export async function loadStudentScopedRecords(userId) {
  const [enrollmentsSnapshot, requestsSnapshot, paymentRequestsSnapshot, paymentsSnapshot] =
    await Promise.all([
      get(query(ref(db, 'enrollments'), orderByChild('studentId'), equalTo(userId))),
      get(
        query(ref(db, 'enrollmentRequests'), orderByChild('studentId'), equalTo(userId)),
      ),
      get(
        query(ref(db, 'paymentRequests'), orderByChild('studentId'), equalTo(userId)),
      ),
      get(query(ref(db, 'payments'), orderByChild('studentId'), equalTo(userId))),
    ]);

  return {
    enrollments: toArray(enrollmentsSnapshot.val()),
    enrollmentRequests: toArray(requestsSnapshot.val()),
    paymentRequests: toArray(paymentRequestsSnapshot.val()),
    payments: toArray(paymentsSnapshot.val()),
  };
}

export async function loadInstructorBaseRaw() {
  const [coursesSnapshot, enrollmentsSnapshot] = await Promise.all([
    get(ref(db, 'courses')),
    get(ref(db, 'enrollments')),
  ]);

  return {
    courses: coursesSnapshot.val() || {},
    enrollments: toArray(enrollmentsSnapshot.val()),
  };
}
